/**
 * Shared gate for `POST /s3/upload-s3-direct` responses.
 *
 * Every funnel photo-upload surface used to do `await response.text()` and
 * store that string as the uploaded file's URL WITHOUT checking
 * `response.ok` first. When the server rejected an upload (e.g. 400 for a
 * disallowed file type), the error STRING became a fileList entry and the UI
 * still showed success — a real patient's estimate was silently blocked this
 * way for 4 days, with junk entries like
 * "File type not allowed without an authenticated session." persisted on her
 * quote.
 *
 * `readUploadedFileKey` is the one place that decides whether a response is
 * actually a usable S3 key: it throws instead of returning anything a caller
 * could mistake for one. Mirrors the check CampaignPhotoContent.tsx has
 * always done inline (its `looksLikeSaneKey`), tightened to the server's
 * exact funnel-upload key shape (see FUNNEL_UPLOAD_KEY_RE in
 * server/routes/s3-routes.ts) so a non-2xx error body can never pass.
 */

export class UploadRejectedError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Upload rejected with status ${status}`);
    this.name = "UploadRejectedError";
    this.status = status;
  }
}

// Mirrors FUNNEL_UPLOAD_KEY_RE (server/routes/s3-routes.ts) — the exact key
// shape uploadToS3Directly generates: `${Date.now()}-${randomUUID()}${ext}`
// = millisecond timestamp, "-", a lowercase-hex v4 UUID, then an optional
// extension. Anchored, so an error-text response body can never satisfy it.
const FUNNEL_UPLOAD_KEY_RE =
  /^\d{10,}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[A-Za-z0-9]{1,10})?$/;

/**
 * Reads the uploaded file's S3 key out of a `/s3/upload-s3-direct` response.
 * Throws `UploadRejectedError` (carrying the HTTP status) when the server
 * rejected the upload, and throws a plain `Error` when the response was 2xx
 * but its body doesn't look like a key this endpoint could have minted —
 * never returns a value a caller could mistake for a real key.
 */
export async function readUploadedFileKey(
  response: Response,
): Promise<string> {
  if (!response.ok) {
    throw new UploadRejectedError(response.status);
  }
  const key = (await response.text()).trim();
  if (!FUNNEL_UPLOAD_KEY_RE.test(key)) {
    throw new Error("Upload response did not look like a valid file key");
  }
  return key;
}
